import {
  Square,
  Squish,
  Heading,
  Stack,
  Row,
  Column,
  Copy,
  Button,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import {
  ISearchParams,
  SearchStatus,
  useGroupSearch,
} from 'src/utilities/useSearchHooks';
import { Pagination } from 'src/components/Pagination/Pagination';
import { Input } from 'src/components/Input/Input';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { getHomeBreadcrumb } from 'src/constants/urls';

interface SearchFormFields {
  groupQuery: string;
}

export default function EnterpriseGroups(): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const { sendTealiumPageView } = useAnalytics();
  const currentQueryText = useRef(null);
  const [isExistingGroupsLoaded, setIsExistingGroupsLoaded] =
    useState<boolean>(false);

  const { register, handleSubmit } = useForm<SearchFormFields>();

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup: 'manage group_home',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    doSearch: doGroupSearch,
    resultsList: groups,
    currentSearchPage,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    totalPages,
    totalResults,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
    searchStatus,
  } = useGroupSearch(corpid as string);

  const corpHasGroups = isExistingGroupsLoaded && !hasNoResultsWithoutQuery;

  useEffect(() => {
    if (corpid) {
      loadGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  async function loadGroups() {
    await doGroupSearch();
    setIsExistingGroupsLoaded(true);
  }

  function searchGroups({ groupQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = { page: 1 };

    if (groupQuery) {
      searchParams.query = groupQuery;
      currentQueryText.current = groupQuery;
    } else {
      currentQueryText.current = null;
    }

    doGroupSearch(searchParams);
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">Groups</Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Row gutter={24}>
            <Column phone={9} gutter={24}>
              <Stack y="x-sm">
                <Heading variant="h4">Groups</Heading>
              </Stack>
              <Copy variant="body">
                View, add and manage groups to create a budget so employees can
                start ordering.
              </Copy>
            </Column>
            <Column phone={3} gutter={24} style={{ textAlign: 'center' }}>
              <RouterButtonLink
                href={`/corporate/enterprise/${corpid}/groups/create`}
                buttonVariant="primary"
                buttonSize="medium"
                useBlockButton
              >
                Add new group
              </RouterButtonLink>
            </Column>
          </Row>
        </PageSection>
      </Stack>
      {corpHasGroups && (
        <PageSection>
          <Stack y="x-sm">
            <Heading variant="h4">Active Groups</Heading>
          </Stack>
          <Stack y="x-sm">
            <Copy variant="body">
              {firstItemNumberFromPage} - {lastItemNumberFromPage} of{' '}
              {`${totalResults} ${getSingularOrPlural(totalResults, 'Group')}`}
            </Copy>
          </Stack>
          <Stack y="md" as="form">
            <Row>
              <Column phone={6}>
                <Row gutter={8}>
                  <Column gutter={8} phone={8}>
                    <Input
                      ariaLabel="Search groups"
                      name="groupQuery"
                      id="groupQuery"
                      ref={register}
                      maxLength={300}
                      placeholder="Group Name"
                    />
                  </Column>
                  <Column gutter={8} phone={4}>
                    <Button
                      variant="primary"
                      block
                      buttonSize="medium"
                      onClick={handleSubmit(searchGroups)}
                    >
                      Search
                    </Button>
                  </Column>
                </Row>
              </Column>
            </Row>
          </Stack>
          <Stack y="md">
            <Table
              isLoading={searchStatus === SearchStatus.LOADING}
              header={() => (
                <Row gutter={24}>
                  <Column gutter={24} phone={3}>
                    <Copy variant="body" color="secondaryText">
                      Name
                    </Copy>
                  </Column>
                  <Column gutter={24} phone={6}>
                    <Copy variant="body" color="secondaryText">
                      Description
                    </Copy>
                  </Column>
                </Row>
              )}
              body={() =>
                totalResults > 0 ? (
                  <ul className="removeListStyles">
                    {groups.map((group) => (
                      <Squish
                        as="li"
                        key={group.id}
                        inset="md"
                        borderBottom="1px solid"
                        borderColor="lightLine"
                      >
                        <Row gutter={24}>
                          <Column gutter={24} phone={3}>
                            {group.name}
                          </Column>
                          <Column gutter={24} phone={6}>
                            {group.description}
                          </Column>

                          <Column
                            phone={3}
                            gutter={24}
                            style={{ textAlign: 'end' }}
                          >
                            <RouterButtonLink
                              href={`/corporate/enterprise/${corpid}/groups/${group.id}/details`}
                              buttonVariant="tertiary"
                              buttonSize="medium"
                            >
                              View group
                            </RouterButtonLink>
                          </Column>
                        </Row>
                      </Squish>
                    ))}
                  </ul>
                ) : (
                  <>
                    {hasNoResultsWithQuery && (
                      <NoResultsMsg
                        message={`No results matching '${currentQueryText.current}'`}
                      />
                    )}
                  </>
                )
              }
              showFooter={false}
            />
          </Stack>
          {totalPages > 1 && (
            <Flex justifyContent="center">
              <Pagination
                boundaryLinks
                page={currentSearchPage}
                collectionSize={totalResults}
                pageChange={(page) => {
                  const searchParams: ISearchParams = { page };
                  if (currentQueryText.current) {
                    searchParams.query = currentQueryText.current;
                  }
                  doGroupSearch(searchParams);
                }}
              />
            </Flex>
          )}
        </PageSection>
      )}
    </Square>
  );
}
